import React, { useCallback, useMemo, useRef, useState } from 'react';
import { registerComponent } from '../../../lib/vulcan-lib/components';
import classNames from 'classnames';
import withErrorBoundary from '../../common/withErrorBoundary';
import { useCurrentUserId, useFilteredCurrentUser } from '../../common/withUser';
import { Link } from '../../../lib/reactRouterWrapper';
import { tagGetCommentLink } from "../../../lib/collections/tags/helpers";
import { AnalyticsContext } from "../../../lib/analyticsEvents";
import type { CommentTreeOptions } from '../commentTree';
import { commentAllowTitle as commentAllowTitle, commentGetPageUrlFromIds } from '../../../lib/collections/comments/helpers';
import { eligibleToNominate, getReviewNameInSitu, shouldShowReviewVotePrompt } from '../../../lib/reviewUtils';
import startCase from 'lodash/startCase';
import FlagIcon from '@/lib/vendor/@material-ui/icons/src/Flag';
import CommentsItemMeta from './CommentsItemMeta';
import { metaNoticeStyles } from "./metaNoticeStyles";
import { getVotingSystemByName } from '../../../lib/voting/getVotingSystem';
import { useVote } from '../../votes/withVote';
import { VotingProps } from '../../votes/votingProps';
import type { TagCommentType } from '@/lib/collections/comments/types';
import type { ContentItemBodyImperative } from '../../contents/contentBodyUtil';
import CommentsEditForm from "../CommentsEditForm";
import CommentExcerpt from "../../common/excerpts/CommentExcerpt";
import CommentBody from "./CommentBody";
import CommentsNewForm from "../CommentsNewForm";
import ParentCommentSingle from "../ParentCommentSingle";
import AnimatedExpansion from "../../common/AnimatedExpansion";
import AnimatedCollapse from "../../common/AnimatedCollapse";
import ForumIcon from "../../common/ForumIcon";
import CommentDiscussionIcon from "./CommentDiscussionIcon";
import LWTooltip from "../../common/LWTooltip";
import PostsTooltip from "../../posts/PostsPreviewTooltip/PostsTooltip";
import ReviewVotingWidget from "../../review/ReviewVotingWidget";
import LWHelpIcon from "../../common/LWHelpIcon";
import CoreTagIcon from "../../tagging/CoreTagIcon";
import HoveredReactionContextProvider from "../../votes/lwReactions/HoveredReactionContextProvider";
import CommentBottom from "./CommentBottom";
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import { useNavigate } from '@/lib/routeUtil';
import dynamic from 'next/dynamic';

const RejectedReasonDisplay = dynamic(() => import("../../sunshineDashboard/RejectedReasonDisplay"));

const styles = defineStyles("CommentsItem", (theme: ThemeType) => ({
  root: {
    paddingLeft: 12,
    paddingRight: 12,
    position: "relative",
    "&:hover .CommentsItemMeta-menu": {
      opacity:1
    }
  },
  subforumTop: {
    paddingTop: 4,
  },
  tagIcon: {
    marginRight: 6,
    '& svg': {
      width: 15,
      height: 15,
      fill: theme.palette.grey[600],
    },
  },
  body: {
    borderStyle: "none",
    padding: 0,
    ...theme.typography.commentStyle,
    '& > .CommentsItemMeta-root + .AnimatedCollapse-root .CommentBody-root.ContentStyles-commentBody': {
      // The animation wrapper prevents these margins from collapsing. The metadata
      // already supplies 8px, so retain only any excess from the body's .5em margin.
      marginTop: 'max(0px, calc(0.5em - 8px))',
    },
  },
  sideComment: {
    "& blockquote": {
      paddingRight: 0,
      marginLeft: 4,
      paddingLeft: 12,
    },
  },
  replyLink: {
    marginRight: 8,
    display: "inline",
    fontWeight: theme.typography.body1.fontWeight,
    color: theme.palette.link.dim,
    "@media print": {
      display: "none",
    },
  },
  firstParentComment: {
    marginLeft: -12,
    marginRight: -12
  },
  replyForm: {
    marginTop: 2,
    marginBottom: 8,
    border: theme.palette.border.normal,
    borderRadius: 0,
  },
  replyFormMinimalist: {
    borderRadius: theme.borderRadius.small,
  },
  deleted: {
    backgroundColor: theme.palette.panelBackground.deletedComment,
  },
  metaNotice: {
    ...metaNoticeStyles(theme),
  },
  pinnedIconWrapper: {
    color: theme.palette.grey[400],
    paddingTop: 10,
    marginBottom: '-3px',
  },
  pinnedIcon: {
    "--icon-size": "12px",
  },
  title: {
    ...theme.typography.display2,
    ...theme.typography.postStyle,
    flexGrow: 1,
    marginTop: 8,
    marginBottom: 0,
    marginLeft: 0,
    display: "block",
    fontSize: '1.5rem',
    lineHeight: '1.5em'
  },
  postTitle: {
    paddingTop: 8,
    ...theme.typography.commentStyle,
    display: "block",
    color: theme.palette.link.dim2,
  },
  reviewVotingButtons: {
    borderTop: theme.palette.border.normal,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: 6,
  },
  updateVoteMessage: {
    ...theme.typography.body2,
    ...theme.typography.smallText,
    color: theme.palette.grey[600]
  },
  postTitleRow: {
    display: 'flex',
    columnGap: 8,
    alignItems: 'center'
  },
  flagIcon: {
    height: 13,
    color: theme.palette.error.main,
    position: "relative",
    top: 3
  },
  excerpt: {
    marginBottom: 8,
  },
}), {
  stylePriority: -1,
});

/**
 * CommentsItem: A single comment, not including any recursion for child comments
 *
 * Before adding more props to this, consider whether you should instead be adding a field to the CommentTreeOptions interface.
 */
export const CommentsItem = ({
  treeOptions,
  comment,
  nestingLevel=1,
  isChild,
  collapsed,
  isParentComment,
  parentCommentId,
  scrollIntoView,
  toggleCollapse,
  setSingleLine,
  truncated,
  showPinnedOnProfile,
  parentAnswerId,
  enableGuidelines=true,
  showParentDefault=false,
  displayTagIcon=false,
  excerptLines,
  className,
}: {
  treeOptions: CommentTreeOptions,
  comment: CommentsList|CommentsListWithParentMetadata,
  nestingLevel: number,
  isChild?: boolean,
  collapsed?: boolean,
  isParentComment?: boolean,
  parentCommentId?: string,
  scrollIntoView?: () => void,
  toggleCollapse?: () => void,
  setSingleLine?: (singleLine: boolean) => void,
  truncated: boolean,
  showPinnedOnProfile?: boolean,
  parentAnswerId?: string,
  enableGuidelines?: boolean,
  showParentDefault?: boolean,
  displayTagIcon?: boolean,
  excerptLines?: number,
  className?: string,
}) => {
  const classes = useStyles(styles);
  const navigate = useNavigate();
  const commentBodyRef = useRef<ContentItemBodyImperative|null>(null); // passed into CommentsItemBody for use in InlineReactSelectionWrapper
  const [replyFormIsOpen, setReplyFormIsOpen] = useState(false);
  const [showEditState, setShowEditState] = useState(treeOptions.initialShowEdit || false);
  const [showParentState, setShowParentState] = useState(showParentDefault);
  const isMinimalist = treeOptions.formStyle === "minimalist"
  const currentUserId = useCurrentUserId();
  const currentUserEligibleToNominate = useFilteredCurrentUser(u => eligibleToNominate(u));

  const {
    postPage, tag, post, refetch, hideReviewVoteButtons,
    hideParentCommentToggleForTopLevel,
  } = treeOptions;

  const showCommentTitle = !!(commentAllowTitle({tagCommentType: comment.tagCommentType as TagCommentType, parentCommentId: comment.parentCommentId}) && comment.title && !comment.deleted && !showEditState)

  const openReplyForm = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setReplyFormIsOpen(true);
  }, []);

  const closeReplyForm = useCallback(() => setReplyFormIsOpen(false), []);

  const replySuccessCallback = () => {
    if (refetch) {
      refetch()
    }
    setReplyFormIsOpen(false);
  }

  const setShowEdit = useCallback(() => {
    setShowEditState(true);
  }, [])

  const editCancelCallback = () => {
    setShowEditState(false);
    if (comment.draft) {
      setSingleLine?.(true);
    }
  }

  const editSuccessCallback = (updatedComment: CommentsList) => {
    if (treeOptions.redirectAfterEditSubmit) {
      navigate(commentGetPageUrlFromIds({
        postId: updatedComment.postId ?? comment.postId,
        commentId: updatedComment._id,
      }));
      return;
    }
    if (refetch) {
      refetch()
    }
    setShowEditState(false);
    if (comment.draft) {
      setSingleLine?.(true);
    }
  }

  const toggleShowParent = useCallback(() => {
    setShowParentState(s => !s);
  }, []);

  const renderBodyOrEditor = (voteProps: VotingProps<VoteableTypeClient>) => {
    if (showEditState) {
      return <CommentsEditForm
        comment={comment}
        successCallback={editSuccessCallback}
        cancelCallback={editCancelCallback}
      />
    } else if (excerptLines) {
      return <CommentExcerpt
        comment={comment}
        lines={excerptLines}
        className={classes.excerpt}
      />
    } else {
      return <CommentBody
        commentBodyRef={commentBodyRef}
        truncated={truncated}
        comment={comment}
        postPage={postPage}
        voteProps={voteProps}
      />
    }
  }

  const renderReply = () => {
    const levelClass = (nestingLevel + (treeOptions.switchAlternatingHighlights ? 0 : 1)) % 2 === 0
      ? "comments-node-even" : "comments-node-odd"

    return (
      <div className={classNames(classes.replyForm, levelClass, isMinimalist && classes.replyFormMinimalist)}>
        <CommentsNewForm
          post={treeOptions.post}
          parentComment={comment}
          successCallback={replySuccessCallback}
          cancelCallback={closeReplyForm}
          prefilledProps={{
            parentAnswerId: parentAnswerId ? parentAnswerId : null
          }}
          interactionType="reply"
          enableGuidelines={enableGuidelines}
          formStyle={treeOptions.formStyle}
        />
      </div>
    )
  }
  const votingSystemName = comment.votingSystem || "default";
  const votingSystem = getVotingSystemByName(votingSystemName);

  const displayReviewVoting =
    !hideReviewVoteButtons
    && shouldShowReviewVotePrompt({
      reviewingForReview: comment.reviewingForReview,
      postAuthorUserId: post?.userId ?? null,
      currentUserId,
      isEligibleToNominate: currentUserEligibleToNominate,
    });

  const voteProps = useVote(comment, "Comments", votingSystem);
  const showInlineCancel = replyFormIsOpen && isMinimalist

  const replacementReplyButton = treeOptions?.replaceReplyButtonsWith?.(comment);
  const replyButton = useMemo(() => (replacementReplyButton || <a
    className={classNames("comments-item-reply-link", classes.replyLink)}
    onClick={showInlineCancel ? closeReplyForm : openReplyForm}
  >
    {showInlineCancel ? "Cancel" : "Reply"}
  </a>), [replacementReplyButton, showInlineCancel, classes.replyLink, openReplyForm, closeReplyForm]);

  return (
    <AnalyticsContext pageElementContext="commentItem" commentId={comment._id}>
    <HoveredReactionContextProvider voteProps={voteProps}>
      <div className={classNames(
        classes.root,
        className,
        "recent-comments-node",
        ((comment.deleted && !comment.deletedPublic) || comment.rejected) && classes.deleted,
        treeOptions.isSideComment && classes.sideComment,
        comment.tagCommentType === "SUBFORUM" && !comment.topLevelCommentId && classes.subforumTop,
      )}>
        { comment.parentCommentId && (
          <div className={classes.firstParentComment}>
            <AnimatedExpansion expanded={showParentState}>
              {showParentState && <ParentCommentSingle
                post={post} tag={tag}
                documentId={comment.parentCommentId}
                nestingLevel={nestingLevel - 1}
                truncated={showParentDefault}
                key={comment.parentCommentId}
                treeOptions={{
                  hideParentCommentToggleForTopLevel,
                }}
              />}
            </AnimatedExpansion>
          </div> 
        )}
        
        <CommentTitleRow comment={comment} treeOptions={treeOptions} isChild={isChild} showPinnedOnProfile={showPinnedOnProfile} />
        <div className={classNames(classes.body)}>
          {showCommentTitle && <div className={classes.title}>
            {(displayTagIcon && tag) ? <span className={classes.tagIcon}>
              <CoreTagIcon tag={tag} />
            </span> : <CommentDiscussionIcon
              comment={comment}
            />}
            {comment.title}
          </div>}
          <CommentsItemMeta
            {...{
              treeOptions,
              comment,
              showCommentTitle,
              isParentComment,
              parentCommentId,
              showParentState,
              toggleShowParent,
              scrollIntoView,
              parentAnswerId,
              setSingleLine,
              collapsed,
              toggleCollapse,
              setShowEdit,
            }}
          />
          {comment.promoted && comment.promotedByUser && <div className={classes.metaNotice}>
            Pinned by {comment.promotedByUser.displayName}
          </div>}
          {comment.rejected && <p>
            <RejectedReasonDisplay reason={comment.rejectedReason ?? null}/>
            {comment.userId === currentUserId && <span className={classes.metaNotice}>
              Hidden from other readers of this thread.
            </span>}
          </p>}
          {comment.deleted ? renderBodyOrEditor(voteProps) : <AnimatedCollapse expanded={!collapsed}>
            {renderBodyOrEditor(voteProps)}
            {!showEditState && <CommentBottom
              comment={comment}
              post={post}
              treeOptions={treeOptions}
              votingSystem={votingSystem}
              voteProps={voteProps}
              commentBodyRef={commentBodyRef}
              replyButton={replyButton}
            />}
          </AnimatedCollapse>}
        </div>
        {(displayReviewVoting || replyFormIsOpen) && <AnimatedCollapse expanded={!collapsed}>
          {displayReviewVoting && <div className={classes.reviewVotingButtons}>
            <div className={classes.updateVoteMessage}>
              <LWTooltip title={`If this review changed your mind, update your ${getReviewNameInSitu()} vote for the original post `}>
                Update your {getReviewNameInSitu()} vote for this post.
                <LWHelpIcon/>
              </LWTooltip>
            </div>
            {post && <ReviewVotingWidget post={post} showTitle={false}/>}
          </div>}
          {replyFormIsOpen && renderReply()}
        </AnimatedCollapse>}
      </div>
    </HoveredReactionContextProvider>
    </AnalyticsContext>
  )
}

const CommentTitleRow = ({comment, treeOptions, isChild, showPinnedOnProfile}: {
  comment: CommentsList|CommentsListWithParentMetadata,
  treeOptions: CommentTreeOptions,
  isChild?: boolean,
  showPinnedOnProfile?: boolean,
}) => {
  const classes = useStyles(styles);
  const { showPostTitle, moderatedCommentId } = treeOptions;

  const visible = (
    (showPinnedOnProfile && comment.isPinnedOnProfile)
    || (moderatedCommentId === comment._id)
    || (showPostTitle && !isChild && hasPostField(comment) && comment.postId && comment.post)
    || (showPostTitle && !isChild && hasTagField(comment) && comment.tag)
  );
  
  if (!visible) return null;

  return <div className={classes.postTitleRow}>
    {showPinnedOnProfile && comment.isPinnedOnProfile && <div className={classes.pinnedIconWrapper}>
      <ForumIcon icon="Pin" className={classes.pinnedIcon} />
    </div>}
    {moderatedCommentId === comment._id && <FlagIcon className={classes.flagIcon} />}
    {showPostTitle && !isChild && hasPostField(comment) && comment.postId && comment.post && <PostsTooltip inlineBlock postId={comment.postId}>
        <Link className={classes.postTitle} to={commentGetPageUrlFromIds({postId: comment.postId, commentId: comment._id, postSlug: ""})}>
          {comment.post.draft && "[Draft] "}
          {comment.post.title}
        </Link>
      </PostsTooltip>}
    {showPostTitle && !isChild && hasTagField(comment) && comment.tag && <Link className={classes.postTitle} to={tagGetCommentLink({tagSlug: comment.tag.slug, tagCommentType: comment.tagCommentType})}>
      {startCase(comment.tag.name)}
    </Link>}
  </div>
}

export default registerComponent(
  'CommentsItem', CommentsItem, {
    hocs: [withErrorBoundary],
    areEqual: {
      treeOptions: "shallow",
    },
  }
);

function hasPostField(comment: CommentsList | CommentsListWithParentMetadata): comment is CommentsListWithParentMetadata {
  return !!(comment as CommentsListWithParentMetadata).post
}

function hasTagField(comment: CommentsList | CommentsListWithParentMetadata): comment is CommentsListWithParentMetadata {
  return !!(comment as CommentsListWithParentMetadata).tag
}
